import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuth0 } from '@auth0/auth0-react'
import { AxiosError } from 'axios'
import { authApi, OnboardingStatus } from '../services/api'

// Dev mode check
const isDevAuthDisabled = import.meta.env.VITE_DISABLE_AUTH === 'true'

export interface OnboardingStatusResult {
  isLoading: boolean
  exists: boolean
  requiresOnboarding: boolean
  requiresPlanSelection: boolean
  requiresConsentReacceptance: boolean
  documentsRequiringReacceptance?: string[]
  subscriptionStatus?: string
  trialEndsAt?: string
  planName?: string
  error: Error | null
  emailVerified: boolean | null // null = still loading
  refetch: () => void
}

/**
 * Hook to check user onboarding status
 *
 * Returns:
 * - exists: true if user is registered in the database
 * - requiresOnboarding: true if user needs to complete organization setup
 * - requiresPlanSelection: true if user needs to select a plan
 * - emailVerified: true if user's email is verified in Auth0, null if still loading
 */
export function useOnboardingStatus(): OnboardingStatusResult {
  const { isAuthenticated, isLoading: authLoading, user } = useAuth0()
  const queryClient = useQueryClient()

  // Check if email is verified from Auth0 user object
  // user is undefined while loading, so we use null to indicate "unknown"
  const emailVerified = authLoading || !user ? null : (user.email_verified ?? false)

  // Determine if we should fetch the status
  // Fetch when: authenticated and not loading auth (regardless of email verification,
  // because paying users may have Active subscription without verified email)
  const shouldFetch = isDevAuthDisabled || (isAuthenticated && !authLoading && emailVerified !== null)

  const { data, isLoading, error } = useQuery<OnboardingStatus, AxiosError>({
    queryKey: ['onboarding-status', isAuthenticated],
    queryFn: () => authApi.getStatus().then(r => r.data),
    enabled: shouldFetch,
    staleTime: 30000, // Consider data fresh for 30 seconds
    retry: 1,
  })

  const handleRefetch = () => {
    queryClient.invalidateQueries({ queryKey: ['onboarding-status'] })
  }

  // In dev mode with disabled auth, assume user exists unless API says otherwise
  if (isDevAuthDisabled && !data && !isLoading) {
    return {
      isLoading: false,
      exists: true,
      requiresOnboarding: false,
      requiresPlanSelection: false,
      requiresConsentReacceptance: false,
      emailVerified: true,
      error: null,
      refetch: handleRefetch,
    }
  }

  // Still loading auth or user object
  if (authLoading || (isAuthenticated && emailVerified === null)) {
    return {
      isLoading: true,
      exists: false,
      requiresOnboarding: false,
      requiresPlanSelection: false,
      requiresConsentReacceptance: false,
      emailVerified: null,
      error: null,
      refetch: handleRefetch,
    }
  }

  // If email is not verified: for paying users (Active subscription),
  // allow through with the API data. For others, block.
  if (isAuthenticated && emailVerified === false) {
    // If we have API data and user has Active subscription, pass through with data
    if (data && data.subscriptionStatus === 'Active') {
      return {
        isLoading: false,
        exists: data.exists,
        requiresOnboarding: data.requiresOnboarding,
        requiresPlanSelection: data.requiresPlanSelection,
        requiresConsentReacceptance: data.requiresConsentReacceptance ?? false,
        documentsRequiringReacceptance: data.documentsRequiringReacceptance,
        subscriptionStatus: data.subscriptionStatus,
        trialEndsAt: data.trialEndsAt,
        planName: data.planName,
        emailVerified: false,
        error: null,
        refetch: handleRefetch,
      }
    }
    // Still loading API data
    if (isLoading) {
      return {
        isLoading: true,
        exists: false,
        requiresOnboarding: false,
        requiresPlanSelection: false,
        requiresConsentReacceptance: false,
        emailVerified: false,
        error: null,
        refetch: handleRefetch,
      }
    }
    // Non-paying user with unverified email
    return {
      isLoading: false,
      exists: data?.exists ?? false,
      requiresOnboarding: false,
      requiresPlanSelection: false,
      requiresConsentReacceptance: false,
      subscriptionStatus: data?.subscriptionStatus,
      emailVerified: false,
      error: null,
      refetch: handleRefetch,
    }
  }

  // Handle API errors - distinguish "user not in DB" from "token expired"
  if (error && !isLoading) {
    const status = error.response?.status
    // 404 = user not found in DB → genuine onboarding needed
    if (status === 404) {
      return {
        isLoading: false,
        exists: false,
        requiresOnboarding: true,
        requiresPlanSelection: false,
        requiresConsentReacceptance: false,
        emailVerified: emailVerified ?? false,
        error: error,
        refetch: handleRefetch,
      }
    }
    // 401 = token expired/invalid → keep loading while Auth0 handles re-auth,
    // rather than asserting exists=false which triggers onboarding redirects
    if (status === 401) {
      return {
        isLoading: true,
        exists: false,
        requiresOnboarding: false,
        requiresPlanSelection: false,
        requiresConsentReacceptance: false,
        emailVerified: emailVerified ?? false,
        error: error,
        refetch: handleRefetch,
      }
    }
    // Network error, timeout, CORS, or server down — surface the error
    // so UI can show an error state instead of spinning forever
    if (!error.response) {
      return {
        isLoading: false,
        exists: false,
        requiresOnboarding: false,
        requiresPlanSelection: false,
        requiresConsentReacceptance: false,
        emailVerified: emailVerified ?? false,
        error: error,
        refetch: handleRefetch,
      }
    }
  }

  return {
    isLoading: authLoading || isLoading,
    exists: data?.exists ?? false,
    requiresOnboarding: data?.requiresOnboarding ?? false,
    requiresPlanSelection: data?.requiresPlanSelection ?? false,
    requiresConsentReacceptance: data?.requiresConsentReacceptance ?? false,
    documentsRequiringReacceptance: data?.documentsRequiringReacceptance,
    subscriptionStatus: data?.subscriptionStatus,
    trialEndsAt: data?.trialEndsAt,
    planName: data?.planName,
    emailVerified: emailVerified ?? false,
    error: error ?? null,
    refetch: handleRefetch,
  }
}
